import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, stageCenterNdc, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getHersheyFont, preloadHersheyFont } from '../hershey-font.js';
import { Adapt } from './lib/adapt.js';
import { vdjBeatBreath, VDJ_PRESETS } from './lib/vdjLook.js';

export const PROFILE = {
  id: 'neonsign',
  family: 'typographic',
  danceability: 0.52,
  requiresTier: 0,
  energyLevel: 0.38,
  bpmMin: 70,
  bpmMax: 125,
  movement: 0.30,
  tonality: 0.85,
  enabled: true
};

// NEONSIGN2 : un mur de briques sombre, la nuit ; des enseignes au néon se
// construisent autour de la pochette - le tube de verre se forme devant nous
// (une pointe orangée de verre chauffé avance le long du tracé), puis
// s'allume trait par trait sur les temps, avec le clignotement typique, et
// reste allumé. Le titre du morceau en cursive, des pictogrammes originaux
// (palmier, cocktail, étoile, onde, coeur, note). LOOK VDJ lounge : la lueur
// respire de 8 % sur le temps, aucun flash.
//
// LES TUBES. Chaque enseigne est un ensemble de tracés : le titre en Hershey
// script (police à TRAIT UNIQUE du domaine public, déjà tracée au centre -
// un néon est un tube plié, pas un contour), les pictogrammes en courbes de
// Bézier écrites ici. Chaque tracé est découpé en segments ; chaque segment
// est un quad CAPSULE (distance au segment calculée dans le fragment), donc
// des jointures rondes et une largeur constante en PIXELS (R1) : coeur blanc
// chaud de 2 px, gaine de gaz de 6 px, halo exp(-d/10) x 0,35 (valeurs
// affichées). Éteint : un tube de verre gris translucide avec son reflet.
// Les couches de lumière sont mélangées en MAX (pas en addition) : deux
// segments qui se recouvrent à une jointure ne font pas de perle brillante.
//
// UNE GÉOMÉTRIE pour toutes les enseignes, reconstruite seulement quand une
// enseigne arrive ou s'en va (une fois par phrase au plus) ; leurs positions
// et échelles, leur état (front de construction, trait en train de
// s'allumer, intensité, gaz) sont des uniforms par enseigne. Les câbles noirs
// vers le transformateur sont calculés dans le vertex shader (une courbe qui
// pend, d'un point de l'enseigne au boîtier) : ils suivent l'enseigne sans
// rien renvoyer au GPU.
//
// LE MUR : briques précalculées une fois dans une texture (joints, teintes,
// usure), éclairées par un CHAMP DE LUMIÈRE : les tubes allumés rendus en
// 1/8 de résolution puis floutés (deux passes) - la lueur des néons colore
// les briques proches, selon la distance (R tier 0 : une teinte globale). Le
// halo visible des tubes est rendu en demi-résolution.
//
// LA MUSIQUE (adapt.js) : temps -> respiration de la lueur ; mesure -> une
// enseigne passe à son autre gaz ; phrase -> une nouvelle enseigne ; drop ->
// toutes s'éteignent puis se rallument ensemble. Défauts vivants : une
// lettre qui grésille, une enseigne qui s'éteint et se rallume.
//
// SCÈNE : à chaque morceau, le titre est construit et ALLUMÉ en moins de 3 s.
// La composition s'organise autour de la pochette (R6, R19) : le titre sous
// elle, les pictogrammes de part et d'autre et en haut à gauche, hors des
// zones d'interface ; la plus récente en grand, les autres un peu plus
// petites ; au plus 4 enseignes (2 au tier 0).

const TIERS = { 0: { maxSigns: 2, lightField: false }, 1: { maxSigns: 4, lightField: true }, 2: { maxSigns: 4, lightField: true } };
const GLASS_PX = 4.5;            // demi-largeur du tube de verre
const SHEATH_PX = 3.6;           // la gaine de gaz (coeur 2 px, gaine 6 px)
const HALO_SRC_PX = 3.0;         // the halo source: the tubes, 6 px wide, at half resolution, then blurred
const HALO_GAIN = 0.55;          // measured: the blurred halo peaks near 0.35 (display) beside the tube
const FIELD_PX = 12;             // les tubes dans le champ de lumière (1/8 de résolution)
const CLIP_PX = 2.2, CABLE_PX = 1.6;
const SEG = 0.035;               // longueur d'un segment, en unités de l'enseigne
const OLD_SCALE = 0.82;          // les enseignes précédentes, un peu plus petites
const BREATH = VDJ_PRESETS.lounge;
const GASES = [[1.0, 0.22, 0.55], [0.18, 0.85, 1.0], [1.0, 0.60, 0.14], [0.30, 1.0, 0.42], [0.62, 0.32, 1.0]];
const PICTOS = ['palm', 'cocktail', 'star', 'wave', 'heart', 'note'];
const TITLE_MAX = 18;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function h11(p) { p = (p * 0.1031) % 1; p *= p + 33.33; p *= p + p; return p % 1; }
// the strike of a whole sign (the shader's strike(), on the CPU): 2-3 irregular flashes, then on
function strikeAll(age, seed) {
  if (age < 0) return 0;
  const a = age / (0.32 + 0.2 * h11(seed));
  if (a >= 1) return 1;
  const st = (e, x) => (x >= e ? 1 : 0);
  const f1 = st(0.05, a) * (1 - st(0.16 + 0.06 * h11(seed + 1), a));
  const f2 = st(0.32 + 0.1 * h11(seed + 2), a) * (1 - st(0.45 + 0.08 * h11(seed + 3), a));
  const f3 = st(0.62 + 0.1 * h11(seed + 4), a) * (h11(seed + 5) > 0.4 ? 1 - st(0.74, a) : 1);
  return Math.min(1, f1 * 0.85 + f2 * 0.7 + f3 + st(0.86, a));
}
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- les pictogrammes : des tracés originaux (Bézier), hauteur ~1, centrés -----------
function cubic(p0, p1, p2, p3, n) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t;
    out.push([u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0], u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1]]);
  }
  return out;
}
function chain(...curves) { const out = []; for (const c of curves) { if (out.length) c.shift(); out.push(...c); } return out; }
function ellipse(cx, cy, rx, ry, rot, n) {
  const out = [];
  for (let i = 0; i <= n; i++) { const a = i / n * Math.PI * 2, x = Math.cos(a) * rx, y = Math.sin(a) * ry; out.push([cx + x * Math.cos(rot) - y * Math.sin(rot), cy + x * Math.sin(rot) + y * Math.cos(rot)]); }
  return out;
}
export function pictogram(name) {
  switch (name) {
    case 'heart': return [chain(cubic([0, -0.5], [-0.18, -0.3], [-0.62, 0.0], [-0.5, 0.3], 14), cubic([-0.5, 0.3], [-0.4, 0.58], [-0.06, 0.56], [0, 0.3], 12),
      cubic([0, 0.3], [0.06, 0.56], [0.4, 0.58], [0.5, 0.3], 12), cubic([0.5, 0.3], [0.62, 0.0], [0.18, -0.3], [0, -0.5], 14))];
    case 'star': { const pts = []; for (let i = 0; i <= 10; i++) { const a = Math.PI / 2 + i * Math.PI / 5, r = i % 2 ? 0.21 : 0.5; pts.push([Math.cos(a) * r, Math.sin(a) * r - 0.03]); } return [pts]; }
    case 'wave': {
      const w = (y0, ph) => { const pts = []; for (let i = 0; i <= 48; i++) { const x = -0.65 + 1.3 * i / 48; pts.push([x, y0 + 0.13 * Math.sin((x * 2.4 + ph) * Math.PI)]); } return pts; };
      return [w(0.17, 0), w(-0.17, 0.35)];
    }
    case 'note': return [
      ellipse(-0.27, -0.36, 0.12, 0.085, 0.35, 20), [[-0.16, -0.32], [-0.16, 0.42]],
      cubic([-0.16, 0.42], [0.0, 0.47], [0.18, 0.48], [0.34, 0.52], 8), [[0.34, 0.52], [0.34, -0.25]],
      ellipse(0.23, -0.29, 0.12, 0.085, 0.35, 20)];
    case 'cocktail': return [
      [[-0.42, 0.38], [0, -0.06], [0.42, 0.38], [-0.42, 0.38]], [[0, -0.06], [0, -0.42]], [[-0.24, -0.45], [0.24, -0.45]],
      ellipse(-0.08, 0.2, 0.06, 0.06, 0, 14), [[-0.3, 0.06], [0.28, 0.55]]];
    case 'palm': return [
      cubic([0.06, -0.52], [0.16, -0.22], [-0.12, 0.06], [-0.01, 0.32], 16),
      cubic([-0.01, 0.32], [-0.2, 0.52], [-0.45, 0.4], [-0.56, 0.18], 12),
      cubic([-0.01, 0.32], [-0.14, 0.58], [-0.34, 0.6], [-0.42, 0.52], 10),
      cubic([-0.01, 0.32], [0.1, 0.6], [0.32, 0.6], [0.4, 0.5], 10),
      cubic([-0.01, 0.32], [0.24, 0.5], [0.48, 0.38], [0.56, 0.16], 12),
      cubic([-0.38, -0.52], [-0.12, -0.42], [0.18, -0.42], [0.42, -0.52], 12)];
    default: return [];
  }
}

// --- le titre en Hershey script -----------------------------------------------------------
export function titleStrokes(font, text) {
  const strokes = [];
  let x = 0;
  for (const ch of text) {
    const g = font.glyphs[ch], adv = font.advances[ch];
    if (g) for (const s of g) if (s.length > 1) strokes.push(s.map((p) => [p[0] + x, p[1]]));
    x += (adv != null ? adv : 0.8) * 1.04;
  }
  return strokes;
}
function shortTitle(t) {
  t = String(t || '').replace(/\s*[([].*?[)\]]\s*/g, ' ').replace(/\s+-\s+.*$/, '').trim();
  if (t.length <= TITLE_MAX) return t;
  const words = t.split(/\s+/); let out = '';
  for (const w of words) { if ((out + ' ' + w).trim().length > TITLE_MAX) break; out = (out + ' ' + w).trim(); }
  return out || t.slice(0, TITLE_MAX);
}

// a set of strokes, centred, with its bbox
function normalise(strokes) {
  let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
  for (const s of strokes) for (const [x, y] of s) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  return { strokes: strokes.map((s) => s.map(([x, y]) => [x - cx, y - cy])), w: x1 - x0, h: y1 - y0 };
}
// resampled into segments of length ~SEG (relative to the sign height)
function resample(stroke, step) {
  const out = [stroke[0]];
  for (let i = 1; i < stroke.length; i++) {
    const a = stroke[i - 1], b = stroke[i], L = Math.hypot(b[0] - a[0], b[1] - a[1]), n = Math.max(1, Math.ceil(L / step));
    for (let k = 1; k <= n; k++) out.push([a[0] + (b[0] - a[0]) * k / n, a[1] + (b[1] - a[1]) * k / n]);
  }
  return out;
}

// --- shaders ------------------------------------------------------------------------------
const tubeVertex = `
  precision highp float;
  attribute vec2 aA;             // segment start (sign units) ; cable: anchor (sign units)
  attribute vec2 aB;             // segment end ; cable: (u0, u1) along the cable
  attribute vec2 aCorner;        // 0 start / 1 end, side -1 / +1
  attribute vec4 aInfo;          // sign, stroke, s0, s1 (arc length along the sign, 0..1)
  attribute float aKind;         // 0 tube, 1 clip, 2 cable
  uniform vec4 uXf[4];           // per sign: offset x, y (height units), scale, alive
  ${SAFE_ZONE_VERTEX_CHUNK}
  uniform vec2 uStageNdc;
  uniform float uPx;             // height units per FULL-resolution pixel
  uniform float uHalfPx;         // this layer's half width, in full-resolution px
  uniform vec2 uKinds;           // the kinds this layer draws: [min, max]
  uniform vec2 uCable[4];        // per sign: where its cable goes (the transformer, or off the right edge)
  varying vec2 vQ;               // along (px, from A), across (px)
  varying float vLen;
  varying float vS;
  varying float vSign;
  varying float vStroke;
  varying float vKind;
  varying float vSafe;           // the cover's own mask: the light dims behind it, wherever it is
  vec2 cablePt(vec2 P0, vec2 P3, float u) {
    // a cable that hangs: two controls dropped below the straight line
    vec2 P1 = P0 + vec2(0.0, -0.04), P2 = P3 + vec2((P0.x - P3.x) * 0.35, 0.0);
    float w = 1.0 - u;
    return w * w * w * P0 + 3.0 * w * w * u * P1 + 3.0 * w * u * u * P2 + u * u * u * P3;
  }
  void main() {
    vec4 xf = vec4(0.0);
    int si = int(aInfo.x + 0.5);
    for (int k = 0; k < 4; k++) if (k == si) xf = uXf[k];
    vSign = aInfo.x; vStroke = aInfo.y; vKind = aKind;
    if (xf.w < 0.5 || aKind < uKinds.x - 0.5 || aKind > uKinds.y + 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec2 A, B;
    if (aKind > 1.5) {
      vec2 anchor = xf.xy + aA * xf.z;
      vec2 to = vec2(0.0);
      for (int k = 0; k < 4; k++) if (k == si) to = uCable[k];
      A = cablePt(anchor, to, aB.x); B = cablePt(anchor, to, aB.y);
    } else { A = xf.xy + aA * xf.z; B = xf.xy + aB * xf.z; }
    vec2 d = B - A; float L = length(d);
    vec2 t = L > 1e-7 ? d / L : vec2(1.0, 0.0), n = vec2(-t.y, t.x);
    float hw = uHalfPx * uPx;
    vec2 P = (aCorner.x < 0.5 ? A - t * hw : B + t * hw) + n * aCorner.y * hw;
    vQ = vec2((aCorner.x < 0.5 ? -hw : L + hw) / uPx, aCorner.y * uHalfPx);
    vLen = L / uPx;
    vS = mix(aInfo.z, aInfo.w, aCorner.x);
    gl_Position = vec4(P.x * 2.0 / uAspect + uStageNdc.x, P.y * 2.0 + uStageNdc.y, 0.0, 1.0);
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;

const SIGN_STATE = `
  uniform vec4 uSt[4];           // build front (0..1), striking stroke, its age (s), intensity
  uniform vec4 uDef[4];          // grésillement : stroke, amount ; gas mix ; heat on
  uniform vec3 uGasA[4];
  uniform vec3 uGasB[4];
  uniform float uTime;
  float h11(float p) { p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
  // the strike of a neon: 2 or 3 irregular flashes, then on
  float strike(float age, float stroke) {
    float a = age / (0.32 + 0.2 * h11(stroke));
    if (a >= 1.0) return 1.0;
    float f1 = step(0.05, a) * (1.0 - step(0.16 + 0.06 * h11(stroke + 1.0), a));
    float f2 = step(0.32 + 0.1 * h11(stroke + 2.0), a) * (1.0 - step(0.45 + 0.08 * h11(stroke + 3.0), a));
    float f3 = step(0.62 + 0.1 * h11(stroke + 4.0), a) * (h11(stroke + 5.0) > 0.4 ? (1.0 - step(0.74, a)) : 1.0);
    float f4 = step(0.86, a);
    return clamp(f1 * 0.85 + f2 * 0.7 + f3 + f4, 0.0, 1.0);
  }
  void signState(float sign, out vec4 st, out vec4 df, out vec3 gas) {
    st = vec4(0.0); df = vec4(0.0); gas = vec3(0.0);
    vec3 ga = vec3(0.0), gb = vec3(0.0);
    for (int k = 0; k < 4; k++) if (abs(float(k) - sign) < 0.5) { st = uSt[k]; df = uDef[k]; ga = uGasA[k]; gb = uGasB[k]; }
    gas = mix(ga, gb, df.z);
  }
  // how lit this stroke is: before the striking one fully, the striking one by its flicker
  float strokeLit(vec4 st, vec4 df, float stroke) {
    float lit = stroke < st.y - 0.5 ? 1.0 : (abs(stroke - st.y) < 0.5 ? strike(st.z, stroke) : 0.0);
    // a letter that crackles
    if (df.y > 0.0 && abs(stroke - df.x) < 0.5) lit *= 1.0 - df.y * step(0.55, h11(floor(uTime * 23.0) + stroke * 7.0));
    return lit * st.w;
  }
`;

// the tube body (verre) : always, once built
const glassFragment = `
  precision highp float;
  ${SIGN_STATE}
  varying vec2 vQ; varying float vLen; varying float vS; varying float vSign; varying float vStroke; varying float vKind; varying float vSafe;
  void main() {
    vec4 st, df; vec3 gas;
    signState(vSign, st, df, gas);
    if (vS > st.x) discard;
    float dx = max(0.0, max(-vQ.x, vQ.x - vLen));
    float d = length(vec2(dx, vQ.y));
    float r = d / ${GLASS_PX.toFixed(1)};
    if (r > 1.25) discard;
    float edge = 1.0 - smoothstep(0.85, 1.15, r);
    // a cylinder: a highlight down one side, a darker lip on the other
    float side = vQ.y / ${GLASS_PX.toFixed(1)};
    float hi = pow(max(0.0, 1.0 - abs(side + 0.45) * 2.2), 4.0);
    float lit = strokeLit(st, df, vStroke);
    vec3 glass = vec3(0.020, 0.022, 0.026) + vec3(0.10, 0.11, 0.12) * hi + vec3(0.012) * (1.0 - r);
    glass += gas * 0.18 * lit;                         // the gas colours the glass a little
    gl_FragColor = vec4(glass * edge, 1.0);
  }
`;

// the gas (gaine) and the core, plus the hot tip while the tube is being formed
const lightFragment = `
  precision highp float;
  ${SIGN_STATE}
  uniform float uBreath;
  varying vec2 vQ; varying float vLen; varying float vS; varying float vSign; varying float vStroke; varying float vKind; varying float vSafe;
  void main() {
    vec4 st, df; vec3 gas;
    signState(vSign, st, df, gas);
    if (vS > st.x) discard;
    float dx = max(0.0, max(-vQ.x, vQ.x - vLen));
    float d = length(vec2(dx, vQ.y));
    float lit = strokeLit(st, df, vStroke) * uBreath * vSafe;
    float core = 1.0 - smoothstep(0.6, 1.4, d);
    float sheath = 1.0 - smoothstep(2.4, ${SHEATH_PX.toFixed(1)}, d);
    vec3 col = gas * sheath * lit + vec3(1.0, 0.94, 0.86) * core * lit;
    // forming: the last few percent of glass behind the front glow orange-hot
    float heat = df.w * smoothstep(st.x - 0.035, st.x, vS) * (1.0 - smoothstep(1.2, 4.0, d));
    col = max(col, vec3(1.0, 0.42, 0.10) * heat * 0.9);
    if (max(col.r, max(col.g, col.b)) < 0.002) discard;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// the halo source, half resolution: the lit tubes, thin and flat; blurred
// afterwards (fixed cost, whatever the number of segments) into the soft halo
const haloFragment = `
  precision highp float;
  ${SIGN_STATE}
  uniform float uBreath;
  uniform float uHaloGain;
  varying vec2 vQ; varying float vLen; varying float vS; varying float vSign; varying float vStroke; varying float vKind; varying float vSafe;
  void main() {
    vec4 st, df; vec3 gas;
    signState(vSign, st, df, gas);
    if (vS > st.x) discard;
    float dx = max(0.0, max(-vQ.x, vQ.x - vLen));
    float d = length(vec2(dx, vQ.y));
    float lit = strokeLit(st, df, vStroke) * uBreath * vSafe;
    gl_FragColor = vec4(gas * lit * uHaloGain * (1.0 - smoothstep(${HALO_SRC_PX.toFixed(1)} - 1.5, ${HALO_SRC_PX.toFixed(1)}, d)), 1.0);
  }
`;

// the light field: the lit tubes, flat, at 1/8 resolution (then blurred)
const fieldFragment = `
  precision highp float;
  ${SIGN_STATE}
  uniform float uBreath;
  varying vec2 vQ; varying float vLen; varying float vS; varying float vSign; varying float vStroke; varying float vKind; varying float vSafe;
  void main() {
    vec4 st, df; vec3 gas;
    signState(vSign, st, df, gas);
    if (vS > st.x) discard;
    float lit = strokeLit(st, df, vStroke) * uBreath * vSafe;
    gl_FragColor = vec4(gas * lit, 1.0);
  }
`;

// the ironmongery: clips and cables, matt black with a faint edge highlight
const mountFragment = `
  precision highp float;
  ${SIGN_STATE}
  uniform float uHalfPxM;
  varying vec2 vQ; varying float vLen; varying float vS; varying float vSign; varying float vStroke; varying float vKind; varying float vSafe;
  void main() {
    vec4 st, df; vec3 gas;
    signState(vSign, st, df, gas);
    if (vKind < 1.5 && vS > st.x) discard;           // clips come with the glass
    float dx = max(0.0, max(-vQ.x, vQ.x - vLen));
    float d = length(vec2(dx, vQ.y));
    float cov = 1.0 - smoothstep(uHalfPxM - 0.6, uHalfPxM + 0.6, d);
    if (cov <= 0.0) discard;
    float hi = pow(max(0.0, 1.0 - abs(vQ.y / uHalfPxM + 0.5) * 2.0), 3.0) * 0.03;
    gl_FragColor = vec4(vec3(0.006 + hi), cov);
  }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const blurFragment = `
  precision highp float;
  uniform sampler2D uTex;
  uniform vec2 uDir;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uTex, vUv).rgb * 0.2270;
    c += (texture2D(uTex, vUv + uDir * 1.3846).rgb + texture2D(uTex, vUv - uDir * 1.3846).rgb) * 0.3162;
    c += (texture2D(uTex, vUv + uDir * 3.2308).rgb + texture2D(uTex, vUv - uDir * 3.2308).rgb) * 0.0703;
    gl_FragColor = vec4(c, 1.0);
  }
`;
const wallFragment = `
  precision highp float;
  uniform sampler2D uBricks;
  uniform sampler2D uHalo;
  uniform sampler2D uField;
  uniform float uHasField;
  uniform vec3 uGlobal;          // tier 0: the signs' mean light, over the whole wall
  uniform float uAspect;
  uniform vec2 uStageCenter;
  uniform vec2 uTransformer;
  varying vec2 vUv;
  float sdBox(vec2 p, vec2 b) { vec2 q = abs(p) - b; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0); }
  void main() {
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    // the bricks: about 30 courses to the height of the frame
    vec3 alb = pow(texture2D(uBricks, p * vec2(0.62, 1.0) * 1.15 + 0.5).rgb, vec3(2.2));
    vec3 field = uHasField > 0.5 ? texture2D(uField, vUv).rgb : uGlobal;
    vec3 halo = texture2D(uHalo, vUv).rgb;
    // ambient night, then the neon light on the bricks (by distance, through the blurred field)
    vec3 col = alb * (0.06 + field * 2.4) + halo;
    // the transformer: a black steel box with a lighter rim and two bolts
    vec2 q = p - uTransformer;
    float db = sdBox(q, vec2(0.050, 0.032));
    if (db < 0.0015) {
      float rim = smoothstep(-0.004, -0.001, db);
      float bolts = step(length(vec2(abs(q.x) - 0.036, q.y - 0.018)), 0.004);
      vec3 box = vec3(0.010) + vec3(0.03) * rim + vec3(0.05) * bolts + field * 0.08;
      col = mix(col, box, 1.0 - smoothstep(0.0, 0.0015, db));
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- the bricks: generated once (canvas), then reused -----------------------------------
let BRICKS = null;
function brickTexture() {
  if (BRICKS) return BRICKS;
  const W = 1024, H = 1024;
  let tex;
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const g = c.getContext('2d');
    let seed = 7;
    const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    g.fillStyle = '#1b1614'; g.fillRect(0, 0, W, H);                   // mortar
    const bw = 128, bh = 48, j = 6;
    for (let row = 0; row < H / bh; row++) {
      const off = row % 2 ? bw / 2 : 0;
      for (let x = -bw; x < W + bw; x += bw) {
        const r = 70 + rnd() * 34, gg = 34 + rnd() * 14, b = 26 + rnd() * 10, k = 0.65 + rnd() * 0.5;
        g.fillStyle = `rgb(${Math.round(r * k)},${Math.round(gg * k)},${Math.round(b * k)})`;
        g.fillRect(x + off + j / 2, row * bh + j / 2, bw - j, bh - j);
        // wear: darker and lighter specks, a chipped corner now and then
        for (let s = 0; s < 40; s++) {
          const sx = x + off + j / 2 + rnd() * (bw - j), sy = row * bh + j / 2 + rnd() * (bh - j), sr = 0.6 + rnd() * 2.4;
          g.fillStyle = rnd() < 0.5 ? 'rgba(0,0,0,0.35)' : 'rgba(255,220,200,0.08)';
          g.beginPath(); g.arc(sx, sy, sr, 0, Math.PI * 2); g.fill();
        }
        if (rnd() < 0.25) { g.fillStyle = '#1b1614'; g.beginPath(); g.arc(x + off + (rnd() < 0.5 ? j : bw - j), row * bh + (rnd() < 0.5 ? j : bh - j), 4 + rnd() * 6, 0, Math.PI * 2); g.fill(); }
        // a soft shading: the top edge catches a little more light
        const grd = g.createLinearGradient(0, row * bh + j / 2, 0, row * bh + bh - j / 2);
        grd.addColorStop(0, 'rgba(255,230,210,0.06)'); grd.addColorStop(1, 'rgba(0,0,0,0.18)');
        g.fillStyle = grd; g.fillRect(x + off + j / 2, row * bh + j / 2, bw - j, bh - j);
      }
    }
    tex = new THREE.CanvasTexture(c);
  } else tex = new THREE.DataTexture(new Uint8Array([40, 20, 16, 255]), 1, 1);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true; tex.needsUpdate = true;
  BRICKS = tex;
  return tex;
}

const _bs = new THREE.Vector2();
function placeTransformer(v, asp) { v.set(-asp / 2 + 0.09, -0.43); }

export class Neonsign2Visualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIERS[this._tier] || TIERS[1];
    const f = urlParam('neonsign');
    this._force = f && (f === 'title' || PICTOS.includes(f)) ? f : null;
    this._flickerDbg = urlParam('neonflicker') === '1';
  }

  // lounge: a soft bloom on the white cores, no RGB split, no flash
  get postProcessing() { return { bloomStrength: 0.35, bloomThreshold: 0.80, aberration: 0, grain: 0.012, vignette: 0.4, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }; }

  init(scene) {
    this._scene = scene;
    this._adapt = new Adapt();
    if (!getHersheyFont()) preloadHersheyFont();
    const xf = [], st = [], df = [], ga = [], gb = [];
    for (let k = 0; k < 4; k++) { xf.push(new THREE.Vector4()); st.push(new THREE.Vector4()); df.push(new THREE.Vector4()); ga.push(new THREE.Vector3()); gb.push(new THREE.Vector3()); }
    this._shared = {
      uXf: { value: xf }, uSt: { value: st }, uDef: { value: df }, uGasA: { value: ga }, uGasB: { value: gb },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather, uStageNdc: { value: new THREE.Vector2() }, uPx: { value: 1 / 720 }, uTime: { value: 0 },
      uTransformer: { value: new THREE.Vector2(-0.76, -0.43) }, uCable: { value: [0, 1, 2, 3].map(() => new THREE.Vector2()) }, uBreath: { value: 1 }
    };
    const mat = (frag, halfPx, kinds, blending, extra) => {
      const u = Object.assign({}, this._shared, { uHalfPx: { value: halfPx }, uKinds: { value: new THREE.Vector2(kinds[0], kinds[1]) } }, extra || {});
      return new THREE.ShaderMaterial({ uniforms: u, vertexShader: tubeVertex, fragmentShader: frag, depthTest: false, depthWrite: false, transparent: true, side: THREE.DoubleSide,
        blending: THREE.CustomBlending, blendEquation: blending === 'max' ? THREE.MaxEquation : THREE.AddEquation,
        blendSrc: blending === 'normal' ? THREE.SrcAlphaFactor : THREE.OneFactor, blendDst: blending === 'normal' ? THREE.OneMinusSrcAlphaFactor : THREE.OneFactor });
    };
    this._mats = {
      mountClips: mat(mountFragment, CLIP_PX, [1, 1], 'normal', { uHalfPxM: { value: CLIP_PX } }),
      mountCables: mat(mountFragment, CABLE_PX, [2, 2], 'normal', { uHalfPxM: { value: CABLE_PX } }),
      glass: mat(glassFragment, GLASS_PX + 1.5, [0, 0], 'max'),
      light: mat(lightFragment, 5.0, [0, 0], 'max'),
      halo: mat(haloFragment, HALO_SRC_PX, [0, 0], 'max', { uHaloGain: { value: HALO_GAIN } }),
      field: mat(fieldFragment, FIELD_PX, [0, 0], 'add')
    };
    this._geo = new THREE.BufferGeometry();
    this._geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    const mesh = (m, order) => { const o = new THREE.Mesh(this._geo, m); o.frustumCulled = false; o.renderOrder = order; return o; };
    this._mCables = mesh(this._mats.mountCables, -998); this._mClips = mesh(this._mats.mountClips, -997);
    this._mGlass = mesh(this._mats.glass, -996); this._mLight = mesh(this._mats.light, -995);
    // the private passes: halo (1/2) and light field (1/8, blurred)
    this._haloScene = new THREE.Scene(); this._haloScene.add(mesh(this._mats.halo, 0));
    this._fieldScene = new THREE.Scene(); this._fieldScene.add(mesh(this._mats.field, 0));
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._blurU = { uTex: { value: null }, uDir: { value: new THREE.Vector2() } };
    this._blurScene = new THREE.Scene();
    const bq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._blurU, vertexShader: quadVertex, fragmentShader: blurFragment, depthTest: false, depthWrite: false }));
    bq.frustumCulled = false; this._blurScene.add(bq);
    this._wallU = {
      uBricks: { value: brickTexture() }, uHalo: { value: null }, uField: { value: null }, uHasField: { value: this._L.lightField ? 1 : 0 },
      uGlobal: { value: new THREE.Vector3() }, uAspect: safeZoneUniforms.uAspect, uStageCenter: safeZoneUniforms.uStageCenter, uTransformer: this._shared.uTransformer
    };
    this._wall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._wallU, vertexShader: quadVertex, fragmentShader: wallFragment, depthTest: false, depthWrite: false }));
    this._wall.frustumCulled = false; this._wall.renderOrder = -1000;
    const self = this;
    this._wall.onBeforeRender = function (renderer) { self._passes(renderer); };
    for (const o of [this._wall, this._mCables, this._mClips, this._mGlass, this._mLight]) scene.add(o);

    this._time = 0; this._signs = []; this._slotTurn = 0; this._trackKey = undefined; this._sinceBeat = 9;
    this._picto = Math.floor(Math.random() * PICTOS.length); this._gasTurn = Math.floor(Math.random() * GASES.length);
    this._barTurn = 0; this._dropAt = -9; this._nextCrackle = 6 + Math.random() * 8; this._nextBlackout = 18 + Math.random() * 15;
    this._rts = null; this._dirty = true;
  }

  // --- the slots around the cover (R6, R19), in height units ---------------------------------
  _slots() {
    const a = safeZoneUniforms.uAspect.value || 1.78;
    const hs = safeZoneUniforms.uStageHalfSize.value;
    const hx = Math.max(0.12, hs.x / 2), hy = Math.max(0.16, Math.min(0.36, hs.y / 2));
    const bottomHalf = Math.max(0.2, 0.28 * a - 0.03);
    return {
      bottom: { cx: 0, cy: (-0.5 + 0.05 + (-hy - 0.03)) / 2, w: 2 * bottomHalf, h: Math.max(0.08, (-hy - 0.03) - (-0.5 + 0.05)) },
      left: { cx: (-a / 2 + 0.05 + (-hx - 0.05)) / 2, cy: 0.0, w: Math.max(0.1, (-hx - 0.05) - (-a / 2 + 0.05)), h: 2 * hy - 0.06 },
      right: { cx: (a / 2 - 0.05 + hx + 0.05) / 2, cy: -0.02, w: Math.max(0.1, (a / 2 - 0.05) - (hx + 0.05)), h: 2 * hy - 0.1 },
      topleft: { cx: (-a / 2 + 0.08 * a + 0.03 + (-0.2 * a - 0.03)) / 2, cy: (hy + 0.03 + 0.5 - 0.03) / 2, w: Math.max(0.08, (-0.2 * a - 0.03) - (-a / 2 + 0.08 * a + 0.03)), h: Math.max(0.06, 0.5 - 0.03 - (hy + 0.03)) }
    };
  }

  // --- a new sign ---------------------------------------------------------------------------
  _addSign(kind, text) {
    let raw;
    if (kind === 'title') {
      const font = getHersheyFont();
      if (!font || !text) return false;
      raw = titleStrokes(font, text);
      if (!raw.length) return false;
    } else raw = pictogram(kind);
    const n = normalise(raw);
    // the slot: the title below the cover, the pictograms around it
    let slot;
    if (kind === 'title') slot = 'bottom';
    else {
      const free = ['left', 'right', 'topleft'].filter((s) => !this._signs.some((g) => g.slot === s && !g.dying));
      slot = free.length ? free[0] : ['left', 'right', 'topleft'][this._slotTurn++ % 3];
    }
    // the slot's previous sign goes out
    for (const g of this._signs) if (g.slot === slot && !g.dying) { g.dying = true; g.dieAt = this._time; }
    const gasA = this._gasTurn++ % GASES.length, gasB = (gasA + 2) % GASES.length;
    const sign = {
      kind, text, slot, n, born: this._time, buildDur: kind === 'title' && this._time - this._trackAt < 2 ? 0.9 : 3.2,
      strike: -1, strikeAt: 0, strikes: 0, gasA, gasB, mix: 0, mixT: 0, intensity: 1, off: 0, dying: false, crackle: 0, crackleStroke: 0, scale: 1
    };
    this._signs.push(sign);
    // too many: the oldest goes out
    const alive = this._signs.filter((g) => !g.dying);
    if (alive.length > this._L.maxSigns) { const o = alive.find((g) => g.kind !== 'title' && g !== sign); if (o) { o.dying = true; o.dieAt = this._time; } }
    this._dirty = true;
    return true;
  }

  // the merged geometry of every sign (rebuilt when a sign arrives or leaves)
  _rebuild() {
    this._dirty = false;
    const signs = this._signs.slice(0, 4);
    const A = [], B = [], C = [], I = [], K = [];
    const quad = (a, b, info, kind) => {
      // two triangles, four corners: (0,-1) (0,1) (1,-1) (1,1)
      const corners = [[0, -1], [0, 1], [1, -1], [0, 1], [1, 1], [1, -1]];
      const s0 = info[2], s1 = info[3];
      for (const c of corners) { A.push(a[0], a[1]); B.push(b[0], b[1]); C.push(c[0], c[1]); I.push(info[0], info[1], s0, s1); K.push(kind); }
    };
    signs.forEach((g, si) => {
      g.index = si;
      const strokes = g.n.strokes.map((s) => resample(s, SEG * Math.max(1, g.n.h)));
      let total = 0;
      for (const s of strokes) for (let i = 1; i < s.length; i++) total += Math.hypot(s[i][0] - s[i - 1][0], s[i][1] - s[i - 1][1]);
      let acc = 0, clipAcc = 0;
      const clipEvery = 0.22 * Math.max(1, g.n.h);
      strokes.forEach((s, k) => {
        for (let i = 1; i < s.length; i++) {
          const L = Math.hypot(s[i][0] - s[i - 1][0], s[i][1] - s[i - 1][1]);
          quad(s[i - 1], s[i], [si, k, acc / total, (acc + L) / total], 0);
          acc += L; clipAcc += L;
          if (clipAcc > clipEvery && i < s.length - 1) {
            clipAcc = 0;
            // a clip: a short bar across the tube, standing off the wall
            const dx = s[i][0] - s[i - 1][0], dy = s[i][1] - s[i - 1][1], l = Math.hypot(dx, dy) || 1;
            const nx = -dy / l * 0.05 * g.n.h, ny = dx / l * 0.05 * g.n.h, f = acc / total;
            quad([s[i][0] - nx, s[i][1] - ny], [s[i][0] + nx, s[i][1] + ny], [si, k, f, f], 1);
          }
        }
      });
      g.strokeCount = strokes.length;
      // the cable: from the bottom of the sign to the transformer, 24 pieces
      const anchor = [0, -g.n.h / 2];
      for (let k = 0; k < 24; k++) quad(anchor, [k / 24, (k + 1) / 24], [si, 0, 0, 0], 2);
    });
    const geo = this._geo;
    for (const name of ['aA', 'aB', 'aCorner', 'aInfo', 'aKind', 'position']) if (geo.getAttribute(name)) geo.deleteAttribute(name);
    const n = K.length;
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geo.setAttribute('aA', new THREE.BufferAttribute(new Float32Array(A), 2));
    geo.setAttribute('aB', new THREE.BufferAttribute(new Float32Array(B), 2));
    geo.setAttribute('aCorner', new THREE.BufferAttribute(new Float32Array(C), 2));
    geo.setAttribute('aInfo', new THREE.BufferAttribute(new Float32Array(I), 4));
    geo.setAttribute('aKind', new THREE.BufferAttribute(new Float32Array(K), 1));
    geo.setDrawRange(0, n);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  }

  onTrackChange(profile) { this._trackKey = undefined; }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING' && !this._flickerDbg) return;          // pause : gel
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    const t = this._time;
    const a = this._adapt.update(dt, profile);
    this._shared.uTime.value = t;
    // a new track: its title, built and lit within 3 s (R20)
    const key = profile.trackId || profile.title;
    if (key !== this._trackKey && (this._force == null || this._force === 'title')) {
      this._trackKey = key; this._trackAt = t;
      if (this._addSign('title', shortTitle(profile.title || (profile.track && profile.track.name) || ''))) this._pendingTitle = null;
      else this._pendingTitle = shortTitle(profile.title || '');
    }
    if (this._pendingTitle && getHersheyFont()) { if (this._addSign('title', this._pendingTitle)) this._pendingTitle = null; }
    if (this._force && this._force !== 'title' && !this._signs.length) this._addSign(this._force);
    // PHRASE: a new sign (a pictogram)
    if ((a.phrase || (this._flickerDbg && t - (this._lastAdd || 0) > 10)) && !this._force) {
      this._lastAdd = t;
      this._addSign(PICTOS[this._picto++ % PICTOS.length]);
    }
    if (!this._signs.length && !this._force && t > 0.5 && !this._pendingTitle) { this._addSign(PICTOS[this._picto++ % PICTOS.length]); }
    // BAR: one sign changes gas
    if (a.bar && this._signs.length) { const lit = this._signs.filter((g) => !g.dying); if (lit.length) { const g = lit[this._barTurn++ % lit.length]; g.mixT = 1 - g.mixT; } }
    // BEAT: the breath of the glow (+8 %)
    if (a.beat) this._sinceBeat = 0; else this._sinceBeat += dt;
    this._shared.uBreath.value = vdjBeatBreath(this._sinceBeat, BREATH);
    // DROP: everything off, then on again together
    if (a.drop) this._dropAt = t;
    const sinceDrop = t - this._dropAt;
    // the living faults
    if (t > this._nextCrackle) { const g = this._signs[Math.floor(Math.random() * this._signs.length)]; if (g && g.strokeCount) { g.crackle = 2.0; g.crackleStroke = Math.floor(Math.random() * g.strokeCount); } this._nextCrackle = t + 10 + Math.random() * 12; }
    if (t > this._nextBlackout) { const g = this._signs.find((x) => !x.dying && x.strike >= x.strokeCount); if (g) { g.off = 0.6; g.relightAt = t + 0.6; } this._nextBlackout = t + 24 + Math.random() * 18; }

    // the signs: build, strike, colour, fade
    for (const g of this._signs) {
      const age = t - g.born;
      g.build = clamp01(age / g.buildDur);
      // strike: after the build, stroke by stroke - on the beats, or every 0.12 s for a fresh title
      if (g.build >= 1 && g.strokeCount) {
        const fast = g.kind === 'title' && g.born - this._trackAt < 0.5;
        const per = Math.max(1, Math.ceil(g.strokeCount / 8));
        if (g.strike < 0) { g.strike = 0; g.strikeAt = t; }
        else if (g.strike < g.strokeCount && (fast ? t - g.strikeAt > 0.09 : (a.beat || t - g.strikeAt > 1.2))) { g.strike = Math.min(g.strokeCount, g.strike + (fast ? Math.ceil(g.strokeCount / 10) : per)); g.strikeAt = t; }
      }
      if (this._flickerDbg && g.strike >= g.strokeCount && t - g.strikeAt > 2.5) { g.strike = 0; g.strikeAt = t; }
      g.mix += (g.mixT - g.mix) * (1 - Math.exp(-dt / 0.35));
      g.crackle = Math.max(0, g.crackle - dt);
      if (g.off > 0) g.off -= dt;
      // off, then on again in one irregular strike (the defect; the drop, all signs together)
      let I = g.off > 0 ? 0 : (g.relightAt != null ? strikeAll(t - g.relightAt, g.born * 7.3) : 1);
      if (sinceDrop >= 0 && sinceDrop < 3) I = Math.min(I, strikeAll(sinceDrop - 0.6, this._dropAt * 3.1));
      if (g.dying) I *= clamp01(1 - (t - g.dieAt) / 0.5);
      g.intensity = I;
    }
    // the dead are removed (geometry rebuilt)
    const before = this._signs.length;
    this._signs = this._signs.filter((g) => !(g.dying && t - g.dieAt > 0.6));
    if (this._signs.length !== before) this._dirty = true;
    if (this._dirty) this._rebuild();

    // placement: each sign fitted into its slot; the newest full size, the others smaller
    const asp = safeZoneUniforms.uAspect.value || 1.78;
    placeTransformer(this._shared.uTransformer.value, asp);
    const slots = this._slots();
    const newest = this._signs.filter((g) => !g.dying).slice(-1)[0];
    const U = this._shared;
    let gr = 0, gg = 0, gb = 0;
    for (let k = 0; k < 4; k++) U.uXf.value[k].w = 0;
    for (const g of this._signs) {
      const k = g.index; if (k == null || k > 3) continue;
      const s = slots[g.slot];
      const fit = Math.min(s.w / Math.max(1e-3, g.n.w), s.h / Math.max(1e-3, g.n.h)) * 0.9;
      const target = g === newest || g.kind === 'title' ? 1 : OLD_SCALE;
      g.scale += (target - g.scale) * (1 - Math.exp(-dt / 0.8));
      U.uXf.value[k].set(s.cx, s.cy, fit * g.scale, 1);
      // the cable: left-hand signs and the title to the transformer, right-hand ones off the right edge
      if (s.cx > 0.05) U.uCable.value[k].set(asp / 2 + 0.05, s.cy - 0.12); else U.uCable.value[k].copy(U.uTransformer.value);
      const lit = g.strokeCount ? clamp01(g.strike / g.strokeCount) : 0;
      U.uSt.value[k].set(g.build, g.strike, t - g.strikeAt, g.intensity);
      U.uDef.value[k].set(g.crackleStroke, g.crackle > 0 ? 1 : 0, g.mix, g.build < 1 ? 1 : 0);
      const A = GASES[g.gasA], Bc = GASES[g.gasB];
      U.uGasA.value[k].set(A[0], A[1], A[2]); U.uGasB.value[k].set(Bc[0], Bc[1], Bc[2]);
      const w = lit * g.intensity;
      gr += (A[0] * (1 - g.mix) + Bc[0] * g.mix) * w; gg += (A[1] * (1 - g.mix) + Bc[1] * g.mix) * w; gb += (A[2] * (1 - g.mix) + Bc[2] * g.mix) * w;
    }
    // tier 0: one light for the whole wall, the signs' mean
    this._wallU.uGlobal.value.set(gr, gg, gb).multiplyScalar(0.035 * this._shared.uBreath.value);
    stageCenterNdc(U.uStageNdc.value);
  }

  // the private passes, once per frame, before the wall
  _passes(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_bs); w = b.x; h = b.y; }
    this._shared.uPx.value = 1 / Math.max(1, h);
    const hw = Math.max(2, Math.round(w / 2)), hh = Math.max(2, Math.round(h / 2));
    const fw = Math.max(2, Math.round(w / 8)), fh = Math.max(2, Math.round(h / 8));
    if (!this._rts || this._rts.halo.width !== hw || this._rts.halo.height !== hh) {
      if (this._rts) for (const k in this._rts) this._rts[k].dispose();
      const o = { depthBuffer: false, stencilBuffer: false, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter };
      this._rts = { halo: new THREE.WebGLRenderTarget(hw, hh, o), h2: new THREE.WebGLRenderTarget(hw, hh, o), f1: new THREE.WebGLRenderTarget(fw, fh, o), f2: new THREE.WebGLRenderTarget(fw, fh, o) };
    }
    const cc = renderer.getClearColor(new THREE.Color()), ca = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._rts.halo); renderer.clear(true, false, false); renderer.render(this._haloScene, this._ortho);
    // the halo: two blur rounds at growing steps - a tight glow and a softer tail (~exp(-d / 10 px))
    this._blur(renderer, this._rts.halo, this._rts.h2, hw, hh, [1.0, 2.2]);
    if (this._L.lightField) {
      renderer.setRenderTarget(this._rts.f1); renderer.clear(true, false, false); renderer.render(this._fieldScene, this._ortho);
      // three blur rounds at growing steps: the light reaches ~250 px around the tubes, softly
      this._blur(renderer, this._rts.f1, this._rts.f2, fw, fh, [1.0, 2.0, 3.5]);
      this._wallU.uField.value = this._rts.f1.texture;
    }
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    this._wallU.uHalo.value = this._rts.halo.texture;
  }

  // separable blur rounds, ping-ponging a -> b -> a
  _blur(renderer, a, b, w, h, steps) {
    for (const k of steps) {
      this._blurU.uTex.value = a.texture; this._blurU.uDir.value.set(k / w, 0);
      renderer.setRenderTarget(b); renderer.render(this._blurScene, this._ortho);
      this._blurU.uTex.value = b.texture; this._blurU.uDir.value.set(0, k / h);
      renderer.setRenderTarget(a); renderer.render(this._blurScene, this._ortho);
    }
  }

  get probe() {
    return { signs: this._signs.map((g) => ({ kind: g.kind, text: g.text, slot: g.slot, build: +(g.build || 0).toFixed(2), strike: g.strike, strokes: g.strokeCount, intensity: g.intensity, mix: +g.mix.toFixed(2) })), font: !!getHersheyFont() };
  }

  dispose() {
    if (this._scene) for (const o of [this._wall, this._mCables, this._mClips, this._mGlass, this._mLight]) this._scene.remove(o);
    if (this._wall) { this._wall.onBeforeRender = null; this._wall.geometry.dispose(); this._wall.material.dispose(); }
    for (const k in (this._mats || {})) this._mats[k].dispose();
    if (this._geo) this._geo.dispose();
    if (this._blurScene) for (const o of this._blurScene.children) { o.geometry.dispose(); o.material.dispose(); }
    if (this._rts) for (const k in this._rts) this._rts[k].dispose();
    this._rts = null; this._scene = null;
  }
}
